import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '../../AuthProvider';
import { Button } from '@/components/ui/button';

export default function AuthModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'client' | 'candidat'>('candidat');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { user } = useAuth();

  const handleAuth = async () => {
    setLoading(true);
    setErrorMsg(null);

    try {
      if (isSignUp) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              role: role,
              user_type: role
            },
          },
        });

        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }

      onClose();
    } catch (error: any) {
      setErrorMsg(error.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setErrorMsg(null);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <img 
              src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-04.png"
              alt="Recruteurs.io"
              className="h-12 mb-4"
            />
            <Dialog.Title className="text-xl font-semibold text-center">
              {isSignUp ? 'Créer un compte' : 'Connexion'}
            </Dialog.Title>
          </div>

          {isSignUp && (
            <>
              <input
                className="w-full mb-3 p-2 border rounded"
                placeholder="Prénom"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <input
                className="w-full mb-3 p-2 border rounded"
                placeholder="Nom"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <div className="mb-3">
                <label htmlFor="role" className="block text-sm font-medium text-neutral-700 mb-1">
                  Vous êtes
                </label>
                <select
                  id="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'client' | 'candidat')}
                  className="w-full border px-3 py-2 rounded-md text-sm text-neutral-800"
                >
                  <option value="candidat">Candidat</option>
                  <option value="client">Entreprise</option>
                </select>
              </div>
            </>
          )}

          <input
            className="w-full mb-3 p-2 border rounded"
            type="email"
            placeholder="Adresse email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <input
            className="w-full mb-4 p-2 border rounded"
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {errorMsg && <p className="text-red-500 text-sm mb-3">{errorMsg}</p>}

          <Button onClick={handleAuth} disabled={loading} className="w-full mb-3">
            {loading ? 'Chargement...' : isSignUp ? "Créer le compte" : "Se connecter"}
          </Button>

          <button
            onClick={toggleMode}
            className="text-sm text-primary underline hover:no-underline"
          >
            {isSignUp ? "Déjà un compte ? Connectez-vous" : "Pas encore de compte ? S'inscrire"}
          </button>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}